import {
  deletePromotionResponseSchema,
  localizedPromotionResponseSchema,
  merchantPromotionItemSchema,
  promotionSchema,
  promotionWriteResponseSchema,
  publishedPromotionCardResponseSchema,
  type DeletePromotionResult,
  type DiscountType,
  type ListResult,
  type LocalizedPromotion,
  type LocalizedText,
  type MerchantPromotionItem,
  type PaginationParams,
  type Promotion,
  type PromotionWriteResult,
  type PromoType,
  type PublishedPromotionCardResponse,
} from '../model';
import {
  deleteOneParsed,
  deleteWithBodyParsed,
  getList,
  getOne,
  patchOne,
  postOne,
  postVoid,
  type RequestOptions,
} from './http';

// Promotion Service — discounts and buy-X-get-Y offers. The merchant-facing
// list (`by-merchant`) returns joined rows (product × restaurant × promotion);
// the per-branch list returns bare promotions.

/** Filters for `GET /merchant/promotions/published` — active published promotions. */
export type ListPublishedPromotionsParams = PaginationParams & {
  restaurant_id?: string;
};

/** Filters for `GET /merchant/promotions`. */
export type ListMerchantPromotionsParams = PaginationParams & {
  active_only?: boolean;
  category_id?: string;
  /** RFC3339 window bounds the promotion must overlap. */
  range_start?: string;
  range_end?: string;
};

/** Filters for `GET /merchant/promotions/by-restaurant`. */
export type ListPromotionsByRestaurantParams = PaginationParams & {
  active_only?: boolean;
};

/**
 * Fields shared by the single- and batch-create bodies.
 *
 * `name` is a `v1LocalizedText`, not a plain string — sending a string fails
 * the whole request with `400 failed to encode payload` (verified live).
 * There is NO `image` member: the v1.00.158 bodies dropped it, and the
 * gateway rejects unknown fields rather than ignoring them.
 */
interface PromotionTermsInput {
  name: LocalizedText;
  promo_type?: PromoType;
  discount_type?: DiscountType;
  discount_value?: number;
  buy_quantity?: number;
  get_quantity?: number;
  /**
   * Promo window bounds.
   *
   * `undefined` OMITS the field. On create that is the documented way to say
   * "Бессрочно"; on UPDATE it means "leave the stored bound alone", which is
   * why ticking «Бессрочно» on an existing dated promo used to save without
   * clearing anything and the old window reappeared.
   *
   * `null` is sent to CLEAR a bound. UNVERIFIED against the live API: the spec
   * types these as plain non-nullable strings and documents no clear semantic
   * for the update body, and the endpoint 401s before body validation so it
   * could not be probed. If the gateway rejects it, this is the line to change
   * — confirm the intended clear signal with the backend team.
   */
  starts_at?: string | null;
  ends_at?: string | null;
}

/** `POST /merchant/promotions` body — one promotion on one branch. */
export interface CreatePromotionInput extends PromotionTermsInput {
  restaurant_id: string;
  product_ids?: string[];
}

/**
 * One branch and the products the promotion covers THERE.
 *
 * The unit of the `selections` body below — see {@link CreatePromotionsInput}
 * for why a per-branch list replaced the old cross product.
 */
export interface PromotionSelection {
  restaurant_id: string;
  product_ids: string[];
}

/**
 * `POST /merchant/promotions/batch` body.
 *
 * TWO mutually exclusive shapes, and mixing them is a 400:
 *
 * - `selections` — per-branch product lists. Branch A can carry three products
 *   while branch B carries one, which is what the wizard's «Филиал продукта»
 *   step collects.
 * - `restaurant_ids` + `product_ids` — the LEGACY cross product: every named
 *   product on every named branch. Still accepted, and still what the mobile
 *   sheet sends, since its pickers cannot express a per-branch set.
 *
 * The cross product could not express "this product only at that branch", so a
 * merchant discounting one item at one location had to create the promotion
 * once per branch. `selections` is the fix.
 */
export type CreatePromotionsInput = PromotionTermsInput &
  (
    | { selections: PromotionSelection[]; restaurant_ids?: never; product_ids?: never }
    | { selections?: never; restaurant_ids: string[]; product_ids?: string[] }
  );

/** `PATCH /merchant/promotions/{id}` body. */
export interface UpdatePromotionInput extends Partial<PromotionTermsInput> {
  is_active?: boolean;
}

export const promotionApi = {
  /**
   * List active published promotions visible to clients — `GET /merchant/promotions/published`.
   * `restaurant_id` is optional.
   */
  listPublished(
    params?: ListPublishedPromotionsParams,
    options?: RequestOptions,
  ): Promise<ListResult<MerchantPromotionItem>> {
    return getList(
      'merchant/promotions/published',
      merchantPromotionItemSchema,
      params,
      options,
    );
  },

  /**
   * Published promotion card — `GET /merchant/promotions/{id}/published`.
   * `restaurant_id` is optional (with it, `product_ids` contains only products of that branch).
   * Answers with `{ client_visible: true, promotion }` or `{ client_visible: false, promotion: null }`.
   */
  getPublished(
    id: string,
    params?: { restaurant_id?: string },
    options?: RequestOptions,
  ): Promise<PublishedPromotionCardResponse> {
    return getOne(
      `merchant/promotions/${id}/published`,
      publishedPromotionCardResponseSchema,
      params,
      options,
    );
  },

  /** List the merchant's promotions as joined product/restaurant/promotion rows. */
  listByMerchant(
    params?: ListMerchantPromotionsParams,
    options?: RequestOptions,
  ): Promise<ListResult<MerchantPromotionItem>> {
    return getList('merchant/promotions', merchantPromotionItemSchema, params, options);
  },

  /** List one branch's merchant-owned promotions. */
  listByRestaurant(
    restaurantId: string,
    params?: ListPromotionsByRestaurantParams,
    options?: RequestOptions,
  ): Promise<ListResult<Promotion>> {
    return getList(
      'merchant/promotions/by-restaurant',
      promotionSchema,
      { ...params, restaurant_id: restaurantId },
      options,
    );
  },

  /**
   * Get a single promotion by id — `GET /merchant/promotions/{id}`, which
   * answers with every translation at once (the client-facing
   * `GET /client/promotions/{id}` is no longer a merchant route).
   *
   * `restaurantId` is REQUIRED, and that is a breaking change: the call without
   * it now 400s. It also changes what comes back — `product_ids` is the set the
   * promotion covers AT THAT BRANCH, not across the campaign, which is exactly
   * what the wizard's per-branch checkboxes need. A promotion spanning several
   * branches therefore takes one read per branch the merchant actually opens.
   */
  async get(
    id: string,
    restaurantId: string,
    options?: RequestOptions,
  ): Promise<LocalizedPromotion> {
    const { promotion } = await getOne(
      `merchant/promotions/${id}`,
      localizedPromotionResponseSchema,
      { restaurant_id: restaurantId },
      options,
    );
    return promotion;
  },

  /**
   * Create a promotion on one branch.
   *
   * Answers with EITHER the created promotion (localized) or only a
   * `pending_change` receipt when the merchant's writes are gated behind
   * moderation — both are successes, so the result is returned as-is.
   */
  create(input: CreatePromotionInput): Promise<PromotionWriteResult> {
    return postOne('merchant/promotions', input, promotionWriteResponseSchema);
  },

  /** Create the same promotion across several branches and products at once. */
  createBatch(input: CreatePromotionsInput): Promise<PromotionWriteResult> {
    return postOne('merchant/promotions/batch', input, promotionWriteResponseSchema);
  },

  /** Update a promotion's terms or activate/deactivate it. */
  update(id: string, input: UpdatePromotionInput): Promise<PromotionWriteResult> {
    return patchOne(`merchant/promotions/${id}`, input, promotionWriteResponseSchema);
  },

  /**
   * Delete a promotion. A gated merchant's delete is QUEUED for moderation —
   * the response then carries a `pending_change` receipt and the row survives
   * until approved, so the receipt is surfaced rather than swallowed.
   */
  remove(id: string): Promise<{ pending: boolean }> {
    return deleteOneParsed(
      `merchant/promotions/${id}`,
      deletePromotionResponseSchema,
    ).then((result) => ({ pending: result.pending_change !== undefined }));
  },

  /** Add products to an existing promotion. */
  addProducts(
    promotionId: string,
    productIds: string[],
    params?: { restaurant_id?: string },
  ): Promise<void> {
    return postVoid(`merchant/promotions/${promotionId}/products`, {
      product_ids: productIds,
      ...params,
    });
  },

  /**
   * Unlink products from a promotion — the campaign and every product NOT
   * named here survive.
   *
   * Ids and the branch go in a JSON BODY now, not the query string. That
   * replaced a route which, through the gateway, did not work at all: the ids
   * were declared as `explode: true` form params, and a single occurrence of
   * `product_ids` failed to decode into the repeated `string[]` — so detaching
   * ONE product, by far the common case, answered `400 failed to encode
   * payload`. The old client duplicated a lone id to force two occurrences;
   * the body shape makes that workaround unnecessary and it is gone.
   *
   * `restaurant_id` narrows the unlink to one branch, and is now part of the
   * body rather than an optional param — matching the per-branch model the
   * whole promotion surface moved to (see {@link PromotionSelection}).
   *
   * Parsed rather than fire-and-forget: like every other promotion write this
   * is moderation-gated, and a queued unlink answers with a `pending_change`
   * receipt instead of applying — swallowing it would report a queued removal
   * as done.
   */
  removeProducts(
    promotionId: string,
    restaurantId: string,
    productIds: string[],
  ): Promise<DeletePromotionResult> {
    return deleteWithBodyParsed(
      `merchant/promotions/${promotionId}/products`,
      deletePromotionResponseSchema,
      { restaurant_id: restaurantId, product_ids: productIds },
    );
  },
};
